import {
  FulfillmentChannelConstraint,
  FulfillmentWindow,
  RequiresActivationConstraintWithLimits,
  AwardRestrictions,
  AwardRestrictionsAwardTypeWatcher,
  PaymentMethodConstraint,
} from '@occc/feat-constraints';
import React from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { Box } from '@mui/material';

import { LoadingOverlay } from '@occc-app/design-system';
import { OfferConfigurationWizardFormLayout } from '@occc-app/routes/CampaignComponents/components/offer-configuration-wizard-form-layout';
import { AwardCount } from '@occc-app/routes/CampaignComponents/components/AwardCount';
import { OfferFilter, Offer_Engagement_Type } from '@occc/graphql';
import { EarningMechanismAwardSelector } from '@occc-app/offerHelpers/components/earning-mechanism-award-selector';
import { AwardType, Flow_Types } from '@occc-app/offerHelpers/types';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';
import { useConfig } from '@occc/config';
import { VoucherExpirationDaysRedemption } from '@occc-app/routes/CampaignComponents/VoucherExpirationDaysRedemption';
import { EitherActivationOrAwardCount } from '@occc-app/routes/Promotion/components/PromotionConfigureForm/EitherActivationOrAwardCountProps';
import { StayVisibleWhileInProgress } from '@occc-app/routes/CampaignComponents/components/StayVisibleWhileInProgress';
import { CampaignComponentReadOnlyFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentReadOnlyFooterActions';
import { CampaignComponentFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentFooterActions';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';
import { OfferEarningMechanismConfigurationSection } from '@occc-app/offerHelpers/components/offer-earning-mechanism-configuration.section';
import { OfferConfigurationSection } from '@occc-app/offerHelpers/components/offer-configuration-section';
import { OfferNameAndDescriptionConfiguration } from '@occc-app/routes/Offer/components/OfferNameAndDescriptionConfiguration';
import { OfferFulfillmentPriorityField } from '@occc-app/offerHelpers/components/OfferFulfillmentPriorityField';

import { PunchcardConfiguration } from './PunchcardConfiguration';
import { validateConfiguration } from '../../validation';
import {
  PunchcardConfigureFormInitialValues,
  PunchcardConfigureFormValues,
  PunchcardConfigureFormValuesPaths,
} from '../../types';
import { CustomData } from '../../../CampaignComponents/components/CustomData/CustomData';
import { configureFormCustomDataSubtitle } from '../../../../offerHelpers/copies';

type PunchcardConfigureFormProps = {
  onSubmit: (
    values: PunchcardConfigureFormValues,
    form: FormApi<PunchcardConfigureFormValues, PunchcardConfigureFormInitialValues>,
  ) => void;
  onCancelClick: (hasUnsavedChanges?: boolean) => void;
  initialValues: PunchcardConfigureFormInitialValues;
  makeOffersFilter: (search: string) => OfferFilter;
  loading: boolean;
  currentFlowType: Flow_Types;
  isFormEditable: boolean;
  onNextStepButtonClick?: () => void;
};

export const PunchcardConfigureForm = ({
  onSubmit,
  initialValues,
  onCancelClick,
  makeOffersFilter,
  loading,
  currentFlowType,
  isFormEditable,
  onNextStepButtonClick,
}: PunchcardConfigureFormProps) => {
  const isOrderAheadEnabled = useConfig('orderAheadConfig').enabled;

  const renderForm: FormRenderProps<any>['render'] = React.useCallback(
    ({
      handleSubmit,
      submitting,
      values,
      form,
      dirty,
    }: FormRenderProps<PunchcardConfigureFormValues>) => {
      const handleCancelClick = () => {
        onCancelClick(dirty);
      };

      const formFooterActionButtons = isFormEditable ? (
        <CampaignComponentFooterActions
          currentFlowType={currentFlowType}
          onCancel={handleCancelClick}
          isSubmitDisabled={submitting || (currentFlowType === Flow_Types.Update && !dirty)}
        />
      ) : (
        <CampaignComponentReadOnlyFooterActions
          onCancel={handleCancelClick}
          onNextStepClick={onNextStepButtonClick}
        />
      );

      const isVoucherRedemptionShown = values.awardType === AwardType.AwardVouchers;
      const isVoucherRedemptionApplicable =
        values.awardVouchers?.voucheredOffer?.engagementType === Offer_Engagement_Type.Vouchered;
      const isVoucherRedemptionEnabled = values.awardVouchers?.isEnabled ?? false;

      const isAwardRestrictionsShown = values.awardType === AwardType.AwardVouchers;

      return (
        <OfferForm dirty={!submitting && dirty} handleSubmit={handleSubmit}>
          <OfferConfigurationWizardFormLayout actions={formFooterActionButtons}>
            <OfferEarningMechanismConfigurationSection>
              <Box mb={2}>
                <OfferNameAndDescriptionConfiguration entityName='Punchcard' />
                <OfferFulfillmentPriorityField mt={2} />
              </Box>
              <EarningMechanismAwardSelector
                buttonTypes={[AwardType.AwardPoints, AwardType.AwardVouchers]}
                makeOffersFilter={makeOffersFilter}
                values={{
                  awardType: values.awardType,
                }}
              />
              <PunchcardConfiguration />
            </OfferEarningMechanismConfigurationSection>

            <OfferConfigurationSection title='Limits'>
              <AwardCount
                values={values}
                shouldShowProgressPerPeriodLimit
                shouldShowProgressPerTransactionLimit
              />
            </OfferConfigurationSection>

            <OfferConfigurationSection title='Requirements'>
              {isVoucherRedemptionShown && (
                <VoucherExpirationDaysRedemption
                  isVoucherRedemptionEnabled={isVoucherRedemptionEnabled}
                  isVoucherRedemptionApplicable={isVoucherRedemptionApplicable}
                  showAvatar
                />
              )}
              <PaymentMethodConstraint values={values} />
              <FulfillmentWindow values={values} />
              <RequiresActivationConstraintWithLimits values={values.activation} />

              {isOrderAheadEnabled && (
                <FulfillmentChannelConstraint values={values.fulfillmentChannelConstraint} />
              )}

              <StayVisibleWhileInProgress
                name={
                  'punchcardChallenge.stayVisibleWhileInProgress.isEnabled' satisfies PunchcardConfigureFormValuesPaths
                }
              />

              {/*region Award Restrictions*/}
              {isAwardRestrictionsShown && <AwardRestrictions />}
              <AwardRestrictionsAwardTypeWatcher awardTypeFieldName='awardType' form={form} />
              {/*endregion*/}
            </OfferConfigurationSection>

            <OfferConfigurationSection title='Advanced Offer Settings' mb={0}>
              <CustomData values={values.customData} subtitle={configureFormCustomDataSubtitle} />
            </OfferConfigurationSection>
          </OfferConfigurationWizardFormLayout>
          <EitherActivationOrAwardCount form={form} />
        </OfferForm>
      );
    },
    [
      isFormEditable,
      currentFlowType,
      onNextStepButtonClick,
      makeOffersFilter,
      onCancelClick,
      isOrderAheadEnabled,
    ],
  );
  return (
    <Box position='relative'>
      <CanEditFormFields isEditable={isFormEditable}>
        <Form<PunchcardConfigureFormValues, PunchcardConfigureFormInitialValues>
          onSubmit={onSubmit}
          render={renderForm}
          mutators={{
            ...arrayMutators,
            setFieldValue: (args, state, utils) => {
              // name, value
              utils.changeValue(state, args[0], () => args[1]);
            },
          }}
          initialValues={initialValues}
          validate={validateConfiguration}
        />
      </CanEditFormFields>
      {loading && <LoadingOverlay rounded />}
    </Box>
  );
};
